import { signedDistanceInDirectionOf } from '@geometry'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

/** Data for creating an additional rip on the merged wall (relative_position only; wall_guid and guid set when creating) */
export interface AdditionalRipToCreate {
  relative_position: string
}

/**
 * Checks if two points are approximately equal (within tolerance)
 */
const pointsEqual = (p1: Point, p2: Point): boolean => {
  const v1 = new ImmutableVector3(p1.x, p1.y, p1.z)
  const v2 = new ImmutableVector3(p2.x, p2.y, p2.z)
  return v1.x === v2.x && v1.y === v2.y && v1.z === v2.z
}

/**
 * Checks if a wall is cartesian oriented (either horizontal or vertical)
 * i.e., wall.shape.points[0].x === wall.shape.points[1].x || wall.shape.points[0].y === wall.shape.points[1].y
 */
export const isCartesianOriented = (wall: ShapeObject): boolean => {
  const p0 = wall.shape.points[0]
  const p1 = wall.shape.points[1]
  return p0.x === p1.x || p0.y === p1.y
}

/**
 * Checks if two walls are cartesian oriented in the same direction
 * Both must be horizontal (same y-coordinate) or both must be vertical (same x-coordinate)
 */
export const areWallsSameCartesianDirection = (wall1: ShapeObject, wall2: ShapeObject): boolean => {
  const w1p0 = wall1.shape.points[0]
  const w1p1 = wall1.shape.points[1]
  const w2p0 = wall2.shape.points[0]
  const w2p1 = wall2.shape.points[1]

  const wall1IsHorizontal = w1p0.y === w1p1.y
  const wall1IsVertical = w1p0.x === w1p1.x
  const wall2IsHorizontal = w2p0.y === w2p1.y
  const wall2IsVertical = w2p0.x === w2p1.x

  // Both must be horizontal or both must be vertical
  return (wall1IsHorizontal && wall2IsHorizontal) || (wall1IsVertical && wall2IsVertical)
}

/**
 * Checks if two walls are adjacent (one wall's start/end point matches the other's start/end point)
 */
export const areWallsAdjacent = (wall1: ShapeObject, wall2: ShapeObject): boolean => {
  const w1p0 = wall1.shape.points[0]
  const w1p1 = wall1.shape.points[1]
  const w2p0 = wall2.shape.points[0]
  const w2p1 = wall2.shape.points[1]

  // Check all possible combinations of adjacency
  return (
    pointsEqual(w1p0, w2p0) ||
    pointsEqual(w1p0, w2p1) ||
    pointsEqual(w1p1, w2p0) ||
    pointsEqual(w1p1, w2p1)
  )
}

/**
 * Creates a copy of an opening (openings are kept in global coordinates on the wall plane)
 */
const copyOpening = (opening: Opening): Opening => {
  return JSON.parse(JSON.stringify(opening))
}

export interface MergeWallData {
  newWall: {
    p1: { x: number; y: number }
    p2: { x: number; y: number }
    storey_idx: number
    placement: WallPlacement
    openings: Opening[]
    additionalRips: AdditionalRipToCreate[]
  }
}

/**
 * Merges two adjacent walls into a single wall
 * The new wall is determined from the points which are not the same.
 * Additional rips from both walls are projected onto the merged wall axis and preserved.
 */
export const mergeWalls = (wall1: ShapeObject, wall2: ShapeObject, rips?: Rip[]): MergeWallData => {
  const w1p0 = wall1.shape.points[0]
  const w1p1 = wall1.shape.points[1]
  const w2p0 = wall2.shape.points[0]
  const w2p1 = wall2.shape.points[1]

  // Find the two unique points (the ones that don't match)
  let p1: Point
  let p2: Point

  if (pointsEqual(w1p0, w2p0)) {
    p1 = w1p1
    p2 = w2p1
  } else if (pointsEqual(w1p0, w2p1)) {
    p1 = w1p1
    p2 = w2p0
  } else if (pointsEqual(w1p1, w2p0)) {
    p1 = w1p0
    p2 = w2p1
  } else if (pointsEqual(w1p1, w2p1)) {
    p1 = w1p0
    p2 = w2p0
  } else {
    throw new Error('Walls are not adjacent')
  }

  // Collect all openings from both walls (excluding local ones)
  const allOpenings: Opening[] = []

  if (wall1.openings) {
    const nonLocalOpenings1 = wall1.openings.filter(opening => !opening.is_local)
    allOpenings.push(...nonLocalOpenings1.map(copyOpening))
  }

  if (wall2.openings) {
    const nonLocalOpenings2 = wall2.openings.filter(opening => !opening.is_local)
    allOpenings.push(...nonLocalOpenings2.map(copyOpening))
  }

  // Collect additional rips from both walls, then project each onto the new wall axis
  const newWallStart = new ImmutableVector3(p1.x, p1.y, p1.z)
  const newWallEnd = new ImmutableVector3(p2.x, p2.y, p2.z)
  const newWallDirection = newWallEnd.sub(newWallStart).normalize()
  const newWallLength = newWallStart.distanceTo(newWallEnd)

  const ripsFromBothWalls: Rip[] = []
  if (rips && wall1.guid) {
    ripsFromBothWalls.push(
      ...rips.filter(
        (rip): rip is Rip => rip.wall_guid === wall1.guid && rip.rip_type === 'AdditionalRip',
      ),
    )
  }
  if (rips && wall2.guid) {
    ripsFromBothWalls.push(
      ...rips.filter(
        (rip): rip is Rip => rip.wall_guid === wall2.guid && rip.rip_type === 'AdditionalRip',
      ),
    )
  }

  const additionalRips: AdditionalRipToCreate[] = []
  if (newWallLength > 0) {
    for (const rip of ripsFromBothWalls) {
      const ripStartVec = new ImmutableVector3(rip.start.x, rip.start.y, rip.start.z)
      const positionAlongNewWall = signedDistanceInDirectionOf(
        newWallStart,
        ripStartVec,
        newWallDirection,
      )
      const relative = Math.min(1, Math.max(0, positionAlongNewWall / newWallLength))
      additionalRips.push({ relative_position: String(relative) })
    }
    additionalRips.sort((a, b) => Number(a.relative_position) - Number(b.relative_position))
  }

  // Determine storey and placement (use wall1's values, but they should be the same)
  const storey_idx = Number(wall1.storey)
  const placement = (wall1.placement || 'Internal') as WallPlacement

  // Create the merged wall
  const newWall = {
    p1: {
      x: p1.x,
      y: p1.y,
    },
    p2: {
      x: p2.x,
      y: p2.y,
    },
    storey_idx,
    placement,
    openings: allOpenings,
    additionalRips,
  }

  return {
    newWall,
  }
}
